import { Navigate, Route, Routes } from "react-router-dom";
import { useAuth } from "react-oidc-context";
import { AppLayout } from "@/components/AppLayout";
import { HomePage } from "@/pages/Home";
import { LoginPage } from "@/pages/Login";
import { AuthCallback } from "@/pages/AuthCallback";
import { PlantillasPage } from "@/pages/admin/PlantillasPage";
import { RequisitosDocumentalesPage } from "@/pages/admin/RequisitosDocumentalesPage";
import { LaboratoriosListPage } from "@/pages/admin/LaboratoriosListPage";
import { LaboratorioAltaPage } from "@/pages/admin/LaboratorioAltaPage";
import { LaboratorioDetailPage } from "@/pages/admin/LaboratorioDetailPage";
import { UsuariosInternosListPage } from "@/pages/admin/UsuariosInternosListPage";
import { UsuariosInternosAltaPage } from "@/pages/admin/UsuariosInternosAltaPage";
import { CalidadPage } from "@/pages/calidad/CalidadPage";
import { TelecomPage } from "@/pages/telecom/TelecomPage";
import { TelecomServiciosMenuPage } from "@/pages/telecom/TelecomServiciosMenuPage";
import { SeleccionCertificadoVigentePage } from "@/pages/telecom/SeleccionCertificadoVigentePage";
import { CartaAduanaAltaPage } from "@/pages/telecom/CartaAduanaAltaPage";
import { CorreccionCartaAduanaAltaPage } from "@/pages/telecom/CorreccionCartaAduanaAltaPage";
import { OpinionTecnicaAltaPage } from "@/pages/telecom/OpinionTecnicaAltaPage";
import { CopiaFielAltaPage } from "@/pages/telecom/CopiaFielAltaPage";
import { ReexpedicionAltaPage } from "@/pages/telecom/ReexpedicionAltaPage";
import { AmpliacionPaisesAltaPage } from "@/pages/telecom/AmpliacionPaisesAltaPage";
import { AmpliacionModelosAltaPage } from "@/pages/telecom/AmpliacionModelosAltaPage";
import { AmpliacionFraccionAltaPage } from "@/pages/telecom/AmpliacionFraccionAltaPage";
import { AmpliacionFilialesAltaPage } from "@/pages/telecom/AmpliacionFilialesAltaPage";
import { SolicitudTelecomAltaPage } from "@/pages/telecom/SolicitudTelecomAltaPage";
import { SolicitudTelecomSuccessPage } from "@/pages/telecom/SolicitudTelecomSuccessPage";
import { SolicitudesPorCertificarPage } from "@/pages/telecom/SolicitudesPorCertificarPage";
import { CertificadoTelecomAltaPage } from "@/pages/telecom/CertificadoTelecomAltaPage";
import { CertificadoTelecomDetailPage } from "@/pages/telecom/CertificadoTelecomDetailPage";
import { RegistrosLegalesListPage } from "@/pages/tramites-legales/RegistrosLegalesListPage";
import { RegistroLegalAltaPage } from "@/pages/tramites-legales/RegistroLegalAltaPage";
import { MiRegistroLegalPage } from "@/pages/mi-registro-legal/MiRegistroLegalPage";
import { MiRegistroLegalWizardPage } from "@/pages/mi-registro-legal/MiRegistroLegalWizardPage";
import { AyudaRegistroLegalPage } from "@/pages/ayuda/AyudaRegistroLegalPage";
import { CentroAyudaPage } from "@/pages/ayuda/CentroAyudaPage";
import { RegistroLegalDetailPage } from "@/pages/detalle/RegistroLegalDetailPage";
import { CartaPoderDetailPage } from "@/pages/detalle/CartaPoderDetailPage";
import { RepresentanteLegalDetailPage } from "@/pages/detalle/RepresentanteLegalDetailPage";
import { SolicitudTelecomDetailPage } from "@/pages/detalle/SolicitudTelecomDetailPage";
import { ExpedienteTelecomPage } from "@/pages/detalle/ExpedienteTelecomPage";
import { SeguridadPage } from "@/pages/seguridad/SeguridadPage";
import { MisAsignadasPage } from "@/pages/bandeja/MisAsignadasPage";
import { ResponsablesEtapaPage } from "@/pages/admin/ResponsablesEtapaPage";
import { SolicitudSeguridadAltaPage } from "@/pages/seguridad/SolicitudSeguridadAltaPage";
import { SolicitudSeguridadSuccessPage } from "@/pages/seguridad/SolicitudSeguridadSuccessPage";
import { SolicitudSeguridadDetailPage } from "@/pages/detalle/SolicitudSeguridadDetailPage";
import { useRolesEfectivos } from "@/lib/sesion";
import { SinAcceso } from "@/components/SinAcceso";
import { useAccesoAmbiente } from "@/lib/ambiente";
import { AccesoAmbienteBloqueado } from "@/components/AccesoAmbienteBloqueado";

/** Usuarios internos del OC — acceso a la bandeja «Mis solicitudes asignadas» (RF-03). */
const ROLES_INTERNOS_OC = [
  "ADMIN_SICEN", "GERENTE_CALIDAD", "AUXILIAR_CALIDAD", "COORDINADOR_TECNICO",
  "EVALUADOR_TECNICO", "RESPONSABLE_TELECOM", "RESPONSABLE_ELECTRICA",
  "RESPONSABLE_EFICIENCIA", "RESPONSABLE_CERTIFICADOS", "FACTURACION",
  "AUDITOR_INTERNO", "DIRECCION", "VIGILANCIA",
];

function RequireAuth({ children }: { children: React.ReactNode }) {
  const auth = useAuth();
  // En un ambiente de pruebas, el backend puede negarle la entrada al personal
  // interno de NPMX que no esté autorizado a probar. Se consulta una vez por
  // sesión y, si la respuesta es «no», se sustituye toda la aplicación por una
  // pantalla que lo explica y lleva al sistema productivo.
  const ambiente = useAccesoAmbiente();
  if (auth.isLoading) {
    return (
      <div className="flex h-screen items-center justify-center text-gray-500">
        Cargando sesión…
      </div>
    );
  }
  if (!auth.isAuthenticated) {
    return <Navigate to="/login" replace />;
  }
  if (ambiente.cargando) {
    return (
      <div className="flex h-screen items-center justify-center text-gray-500">
        Verificando acceso…
      </div>
    );
  }
  if (ambiente.bloqueado) {
    return <AccesoAmbienteBloqueado acceso={ambiente.acceso} />;
  }
  return <>{children}</>;
}

/** Decodifica el payload del JWT (base64url) sin validar firma — solo para lectura de claims en UI. */
function RequireRole({ role, children }: { role: string; children: React.ReactNode }) {
  // Roles EFECTIVOS: durante una suplantación el acceso a las secciones debe
  // reflejar lo que puede el usuario suplantado, no lo que puede el token.
  if (!useRolesEfectivos().includes(role)) {
    return <SinAcceso roles={[role]} />;
  }
  return <>{children}</>;
}

function RequireAnyRole({ roles, children }: { roles: string[]; children: React.ReactNode }) {
  const userRoles = useRolesEfectivos();
  if (!roles.some((r) => userRoles.includes(r))) {
    return <SinAcceso roles={roles} />;
  }
  return <>{children}</>;
}

export function App() {
  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route path="/oidc-callback" element={<AuthCallback />} />
      {/* Centro de Ayuda — accesible sin sesión para clientes prospectos y soporte. */}
      <Route path="/ayuda" element={<CentroAyudaPage />} />
      <Route
        path="/"
        element={
          <RequireAuth>
            <AppLayout />
          </RequireAuth>
        }
      >
        <Route index element={<HomePage />} />
        <Route
          path="mis-asignadas"
          element={
            <RequireAnyRole roles={ROLES_INTERNOS_OC}>
              <MisAsignadasPage />
            </RequireAnyRole>
          }
        />
        <Route
          path="admin/responsables-etapa"
          element={
            <RequireRole role="ADMIN_SICEN">
              <ResponsablesEtapaPage />
            </RequireRole>
          }
        />
        <Route
          path="admin/plantillas"
          element={
            <RequireRole role="ADMIN_SICEN">
              <PlantillasPage />
            </RequireRole>
          }
        />
        <Route
          path="admin/requisitos-documentales"
          element={
            <RequireAnyRole roles={["GERENTE_CALIDAD", "ADMIN_SICEN"]}>
              <RequisitosDocumentalesPage />
            </RequireAnyRole>
          }
        />
        <Route
          path="admin/usuarios"
          element={
            <RequireAnyRole roles={["ADMIN_SICEN", "GERENTE_CALIDAD", "AUXILIAR_CALIDAD"]}>
              <UsuariosInternosListPage />
            </RequireAnyRole>
          }
        />
        <Route
          path="admin/usuarios/nuevo"
          element={
            <RequireAnyRole roles={["ADMIN_SICEN", "GERENTE_CALIDAD", "AUXILIAR_CALIDAD"]}>
              <UsuariosInternosAltaPage />
            </RequireAnyRole>
          }
        />
        <Route
          path="admin/laboratorios"
          element={
            <RequireRole role="ADMIN_SICEN">
              <LaboratoriosListPage />
            </RequireRole>
          }
        />
        <Route
          path="admin/laboratorios/nuevo"
          element={
            <RequireRole role="ADMIN_SICEN">
              <LaboratorioAltaPage />
            </RequireRole>
          }
        />
        <Route
          path="admin/laboratorios/:id"
          element={
            <RequireRole role="ADMIN_SICEN">
              <LaboratorioDetailPage />
            </RequireRole>
          }
        />
        <Route
          path="calidad"
          element={
            <RequireAnyRole roles={["GERENTE_CALIDAD", "AUXILIAR_CALIDAD", "ADMIN_SICEN"]}>
              <CalidadPage />
            </RequireAnyRole>
          }
        />
        <Route
          path="tramites-legales"
          element={
            <RequireAnyRole roles={["GERENTE_CALIDAD", "AUXILIAR_CALIDAD", "ADMIN_SICEN"]}>
              <RegistrosLegalesListPage />
            </RequireAnyRole>
          }
        />
        <Route
          path="tramites-legales/alta"
          element={
            <RequireAnyRole roles={["GERENTE_CALIDAD", "AUXILIAR_CALIDAD", "ADMIN_SICEN"]}>
              <RegistroLegalAltaPage />
            </RequireAnyRole>
          }
        />
        <Route
          path="ayuda/registro-legal"
          element={<AyudaRegistroLegalPage />}
        />
        <Route
          path="mi-registro-legal"
          element={
            <RequireAnyRole
              roles={[
                "TITULAR", "TRAMITADOR", "USUARIO_GESTOR", "REPRESENTANTE_LEGAL",
                "GERENTE_CALIDAD", "AUXILIAR_CALIDAD", "ADMIN_SICEN",
              ]}
            >
              <MiRegistroLegalPage />
            </RequireAnyRole>
          }
        />
        <Route
          path="mi-registro-legal/wizard"
          element={
            <RequireAnyRole
              roles={[
                "TITULAR", "TRAMITADOR", "USUARIO_GESTOR", "REPRESENTANTE_LEGAL",
                "GERENTE_CALIDAD", "AUXILIAR_CALIDAD", "ADMIN_SICEN",
              ]}
            >
              <MiRegistroLegalWizardPage />
            </RequireAnyRole>
          }
        />
        <Route
          path="telecom"
          element={
            <RequireAnyRole
              roles={[
                "RESPONSABLE_TELECOM",
                "EVALUADOR_TECNICO",
                "COORDINADOR_TECNICO",
                "AUDITOR_INTERNO",
                "TRAMITADOR",
                "TITULAR",
                "USUARIO_GESTOR",
                "REPRESENTANTE_LEGAL",
                "FACTURACION",
                "ADMIN_SICEN",
              ]}
            >
              <TelecomPage />
            </RequireAnyRole>
          }
        />
        <Route
          path="sector/telecom/solicitudes/nueva"
          element={
            <RequireAnyRole roles={["TRAMITADOR", "TITULAR", "USUARIO_GESTOR", "REPRESENTANTE_LEGAL", "RESPONSABLE_TELECOM", "ADMIN_SICEN"]}>
              <SolicitudTelecomAltaPage />
            </RequireAnyRole>
          }
        />
        <Route
          path="telecom/servicios"
          element={
            <RequireAnyRole roles={["TRAMITADOR", "TITULAR", "USUARIO_GESTOR", "REPRESENTANTE_LEGAL", "RESPONSABLE_TELECOM", "ADMIN_SICEN"]}>
              <TelecomServiciosMenuPage />
            </RequireAnyRole>
          }
        />
        <Route
          path="telecom/servicios/seleccionar-certificado"
          element={
            <RequireAnyRole roles={["TRAMITADOR", "TITULAR", "USUARIO_GESTOR", "REPRESENTANTE_LEGAL", "RESPONSABLE_TELECOM", "ADMIN_SICEN"]}>
              <SeleccionCertificadoVigentePage />
            </RequireAnyRole>
          }
        />
        <Route
          path="telecom/servicios/carta-aduana"
          element={
            <RequireAnyRole roles={["TRAMITADOR", "TITULAR", "USUARIO_GESTOR", "REPRESENTANTE_LEGAL", "RESPONSABLE_TELECOM", "ADMIN_SICEN"]}>
              <CartaAduanaAltaPage />
            </RequireAnyRole>
          }
        />
        <Route
          path="telecom/servicios/correccion-carta-aduana"
          element={
            <RequireAnyRole roles={["TRAMITADOR", "TITULAR", "USUARIO_GESTOR", "REPRESENTANTE_LEGAL", "RESPONSABLE_TELECOM", "ADMIN_SICEN"]}>
              <CorreccionCartaAduanaAltaPage />
            </RequireAnyRole>
          }
        />
        <Route
          path="telecom/servicios/opinion-tecnica"
          element={
            <RequireAnyRole roles={["TRAMITADOR", "TITULAR", "USUARIO_GESTOR", "REPRESENTANTE_LEGAL", "RESPONSABLE_TELECOM", "ADMIN_SICEN"]}>
              <OpinionTecnicaAltaPage />
            </RequireAnyRole>
          }
        />
        <Route
          path="telecom/servicios/copia-fiel"
          element={
            <RequireAnyRole roles={["TRAMITADOR", "TITULAR", "USUARIO_GESTOR", "REPRESENTANTE_LEGAL", "RESPONSABLE_TELECOM", "ADMIN_SICEN"]}>
              <CopiaFielAltaPage />
            </RequireAnyRole>
          }
        />
        <Route
          path="telecom/servicios/reexpedicion"
          element={
            <RequireAnyRole roles={["TRAMITADOR", "TITULAR", "USUARIO_GESTOR", "REPRESENTANTE_LEGAL", "RESPONSABLE_TELECOM", "ADMIN_SICEN"]}>
              <ReexpedicionAltaPage />
            </RequireAnyRole>
          }
        />
        <Route
          path="telecom/servicios/ampliacion-paises"
          element={
            <RequireAnyRole roles={["TRAMITADOR", "TITULAR", "USUARIO_GESTOR", "REPRESENTANTE_LEGAL", "RESPONSABLE_TELECOM", "ADMIN_SICEN"]}>
              <AmpliacionPaisesAltaPage />
            </RequireAnyRole>
          }
        />
        <Route
          path="telecom/servicios/ampliacion-modelos"
          element={
            <RequireAnyRole roles={["TRAMITADOR", "TITULAR", "USUARIO_GESTOR", "REPRESENTANTE_LEGAL", "RESPONSABLE_TELECOM", "ADMIN_SICEN"]}>
              <AmpliacionModelosAltaPage />
            </RequireAnyRole>
          }
        />
        <Route
          path="telecom/servicios/ampliacion-fraccion"
          element={
            <RequireAnyRole roles={["TRAMITADOR", "TITULAR", "USUARIO_GESTOR", "REPRESENTANTE_LEGAL", "RESPONSABLE_TELECOM", "ADMIN_SICEN"]}>
              <AmpliacionFraccionAltaPage />
            </RequireAnyRole>
          }
        />
        <Route
          path="telecom/servicios/ampliacion-filiales"
          element={
            <RequireAnyRole roles={["TRAMITADOR", "TITULAR", "USUARIO_GESTOR", "REPRESENTANTE_LEGAL", "RESPONSABLE_TELECOM", "ADMIN_SICEN"]}>
              <AmpliacionFilialesAltaPage />
            </RequireAnyRole>
          }
        />
        <Route
          path="telecom/certificados/por-certificar"
          element={
            <RequireAnyRole roles={["ADMIN_SICEN","RESPONSABLE_TELECOM","COORDINADOR_TECNICO"]}>
              <SolicitudesPorCertificarPage />
            </RequireAnyRole>
          }
        />
        <Route
          path="telecom/certificados/nuevo"
          element={
            <RequireAnyRole roles={["ADMIN_SICEN","RESPONSABLE_TELECOM","COORDINADOR_TECNICO"]}>
              <CertificadoTelecomAltaPage />
            </RequireAnyRole>
          }
        />
        <Route
          path="telecom/certificados/:id"
          element={
            <RequireAnyRole roles={["ADMIN_SICEN","RESPONSABLE_TELECOM","COORDINADOR_TECNICO","EVALUADOR_TECNICO","AUDITOR_INTERNO","GERENTE_CALIDAD","AUXILIAR_CALIDAD","TITULAR","TRAMITADOR","USUARIO_GESTOR","REPRESENTANTE_LEGAL"]}>
              <CertificadoTelecomDetailPage />
            </RequireAnyRole>
          }
        />
        <Route
          path="telecom/certificados/:id/success"
          element={
            <RequireAnyRole roles={["ADMIN_SICEN","RESPONSABLE_TELECOM","COORDINADOR_TECNICO"]}>
              <CertificadoTelecomDetailPage />
            </RequireAnyRole>
          }
        />
        <Route
          path="sector/telecom/solicitudes/:id/success"
          element={
            <RequireAnyRole roles={["TRAMITADOR", "TITULAR", "USUARIO_GESTOR", "REPRESENTANTE_LEGAL", "RESPONSABLE_TELECOM", "EVALUADOR_TECNICO", "COORDINADOR_TECNICO", "AUDITOR_INTERNO", "ADMIN_SICEN"]}>
              <SolicitudTelecomSuccessPage />
            </RequireAnyRole>
          }
        />
        <Route
          path="registros-legales/:id"
          element={
            <RequireAnyRole roles={["GERENTE_CALIDAD", "AUXILIAR_CALIDAD", "ADMIN_SICEN"]}>
              <RegistroLegalDetailPage />
            </RequireAnyRole>
          }
        />
        <Route
          path="cartas-poder/:id"
          element={
            <RequireAnyRole roles={["GERENTE_CALIDAD", "AUXILIAR_CALIDAD", "ADMIN_SICEN"]}>
              <CartaPoderDetailPage />
            </RequireAnyRole>
          }
        />
        <Route
          path="representantes-legales/:id"
          element={
            <RequireAnyRole roles={["GERENTE_CALIDAD", "AUXILIAR_CALIDAD", "ADMIN_SICEN"]}>
              <RepresentanteLegalDetailPage />
            </RequireAnyRole>
          }
        />
        <Route
          path="sector/telecom/solicitudes/:id"
          element={
            <RequireAnyRole
              roles={[
                "RESPONSABLE_TELECOM",
                "EVALUADOR_TECNICO",
                "COORDINADOR_TECNICO",
                "AUDITOR_INTERNO",
                "TRAMITADOR",
                "TITULAR",
                "USUARIO_GESTOR",
                "REPRESENTANTE_LEGAL",
                "FACTURACION",
                "GERENTE_CALIDAD",
                "AUXILIAR_CALIDAD",
                "ADMIN_SICEN",
              ]}
            >
              <SolicitudTelecomDetailPage />
            </RequireAnyRole>
          }
        />
        <Route
          path="sector/telecom/expedientes/por-solicitud/:solicitudId"
          element={
            <RequireAnyRole
              roles={[
                "RESPONSABLE_TELECOM",
                "EVALUADOR_TECNICO",
                "COORDINADOR_TECNICO",
                "AUDITOR_INTERNO",
                "TRAMITADOR",
                "TITULAR",
                "USUARIO_GESTOR",
                "REPRESENTANTE_LEGAL",
                "ADMIN_SICEN",
              ]}
            >
              <ExpedienteTelecomPage />
            </RequireAnyRole>
          }
        />
        {/* ===== Sector Seguridad (solución temporal sobre el flujo Telecom) ===== */}
        <Route
          path="seguridad"
          element={
            <RequireAnyRole
              roles={[
                "RESPONSABLE_TELECOM",
                "EVALUADOR_TECNICO",
                "COORDINADOR_TECNICO",
                "AUDITOR_INTERNO",
                "TRAMITADOR",
                "TITULAR",
                "USUARIO_GESTOR",
                "REPRESENTANTE_LEGAL",
                "FACTURACION",
                "ADMIN_SICEN",
              ]}
            >
              <SeguridadPage />
            </RequireAnyRole>
          }
        />
        <Route
          path="sector/seguridad/solicitudes/nueva"
          element={
            <RequireAnyRole roles={["TRAMITADOR", "TITULAR", "USUARIO_GESTOR", "REPRESENTANTE_LEGAL", "RESPONSABLE_TELECOM", "ADMIN_SICEN"]}>
              <SolicitudSeguridadAltaPage />
            </RequireAnyRole>
          }
        />
        <Route
          path="sector/seguridad/solicitudes/:id/success"
          element={
            <RequireAnyRole roles={["TRAMITADOR", "TITULAR", "USUARIO_GESTOR", "REPRESENTANTE_LEGAL", "RESPONSABLE_TELECOM", "EVALUADOR_TECNICO", "COORDINADOR_TECNICO", "AUDITOR_INTERNO", "ADMIN_SICEN"]}>
              <SolicitudSeguridadSuccessPage />
            </RequireAnyRole>
          }
        />
        <Route
          path="sector/seguridad/solicitudes/:id"
          element={
            <RequireAnyRole
              roles={[
                "RESPONSABLE_TELECOM",
                "EVALUADOR_TECNICO",
                "COORDINADOR_TECNICO",
                "AUDITOR_INTERNO",
                "TRAMITADOR",
                "TITULAR",
                "USUARIO_GESTOR",
                "REPRESENTANTE_LEGAL",
                "FACTURACION",
                "GERENTE_CALIDAD",
                "AUXILIAR_CALIDAD",
                "ADMIN_SICEN",
              ]}
            >
              <SolicitudSeguridadDetailPage />
            </RequireAnyRole>
          }
        />
      </Route>
    </Routes>
  );
}
